import React, { useEffect, useState, useMemo, useRef } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { AthleteAvatar } from '../components/AthleteAvatar';
import { toast } from 'sonner';
import { Search, Printer, LayoutGrid, List, Camera } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SQUADS = ['U15', 'U17', 'U19', 'U21', 'Senior', 'Reserve'];

const ageFromDob = (dob) => {
  if (!dob) return null;
  const today = new Date();
  const b = new Date(dob);
  if (Number.isNaN(b.getTime())) return null;
  let age = today.getFullYear() - b.getFullYear();
  const m = today.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < b.getDate())) age--;
  return age;
};

/**
 * Tiny inline photo-upload button used on every roster card/row.
 * Reuses the same backend endpoint as ProfilePhotoUploader. After a successful
 * upload it pushes the new thumbnail URL back to the parent so the avatar
 * updates immediately without a refetch.
 */
const RosterPhotoButton = ({ athleteId, athleteName, onUploaded, variant = 'overlay' }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handlePick = (e) => {
    e.stopPropagation();
    inputRef.current?.click();
  };

  const handleFile = async (e) => {
    e.stopPropagation();
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Photo must be under 5 MB');
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/athletes/${athleteId}/profile-photo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onUploaded?.({
        profile_photo: res.data.profile_photo,
        profile_photo_thumbnail: res.data.profile_photo_thumbnail,
      });
      toast.success(`Photo updated for ${athleteName}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  const stopAll = (e) => e.stopPropagation();

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept=".jpg,.jpeg,.png,.webp"
        className="hidden"
        onClick={stopAll}
        onChange={handleFile}
        data-testid={`roster-photo-input-${athleteId}`}
      />
      <button
        type="button"
        onClick={handlePick}
        onMouseDown={stopAll}
        disabled={busy}
        title={busy ? 'Uploading…' : 'Upload / change photo'}
        className={
          variant === 'overlay'
            ? 'absolute top-1.5 right-1.5 inline-flex items-center justify-center h-6 w-6 rounded-full bg-white/95 dark:bg-gray-900/95 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 shadow-sm opacity-0 group-hover:opacity-100 focus:opacity-100 transition disabled:opacity-50'
            : 'inline-flex items-center gap-1 h-7 px-2 rounded-md text-[11px] font-medium border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50'
        }
        data-testid={`roster-upload-photo-${athleteId}`}
      >
        <Camera className="w-3.5 h-3.5" />
        {variant === 'inline' && <span>{busy ? 'Uploading…' : 'Photo'}</span>}
      </button>
    </>
  );
};

const VisualRoster = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [athletes, setAthletes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [squad, setSquad] = useState('');
  const [position, setPosition] = useState('');
  const [nationality, setNationality] = useState('');
  const [view, setView] = useState('grid'); // 'grid' | 'list'

  const isStaffOrAdmin = user?.role === 'Admin' || user?.role === 'Staff';
  // Photo management RBAC mirrors the backend rule:
  //  Admin → can manage every athlete.
  //  Staff → only assigned athletes. /api/athletes/visual-roster is already
  //          scoped to the staff's assigned athletes server-side, so every
  //          athlete in `athletes` is manageable by Staff who can see it.
  const canManagePhotos = user?.role === 'Admin' || user?.role === 'Staff';

  useEffect(() => {
    if (!isStaffOrAdmin) return;
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (squad) params.append('squad', squad);
        if (position) params.append('position', position);
        if (nationality) params.append('nationality', nationality);
        if (search) params.append('search', search);
        const res = await axios.get(`${API}/athletes/visual-roster?${params.toString()}`);
        setAthletes(res.data.athletes || []);
      } catch {
        toast.error('Failed to load roster');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [squad, position, nationality, search, isStaffOrAdmin]);

  const distinctPositions = useMemo(() => Array.from(new Set(athletes.map(a => a.position).filter(Boolean))).sort(), [athletes]);
  const distinctNationalities = useMemo(() => Array.from(new Set(athletes.map(a => a.nationality).filter(Boolean))).sort(), [athletes]);

  const handlePrint = () => {
    window.print();
  };

  const openAthlete = (a) => {
    if (!a?.id) {
      toast.error('Athlete record is missing an id');
      return;
    }
    navigate(`/athlete/${a.id}/360`);
  };

  const handleUploaded = (athleteId, fields) => {
    setAthletes(prev => prev.map(a => a.id === athleteId ? { ...a, ...fields } : a));
  };

  if (!isStaffOrAdmin) {
    return (
      <Layout>
        <div className="p-8 text-center text-gray-500">Access restricted.</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
          .print-card { break-inside: avoid; }
        }
      `}</style>
      <div className="p-6 lg:p-8">
        <div className="mb-6 flex items-start justify-between gap-4 no-print">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Visual Roster
            </h2>
            <p className="text-gray-600 dark:text-gray-500 mt-1">
              Face sheet — Admin &amp; Staff only. Useful when you recognise athletes by face but not by name.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant={view === 'grid' ? 'default' : 'outline'} size="sm"
              onClick={() => setView('grid')} data-testid="view-grid"
            ><LayoutGrid className="w-4 h-4 mr-1" /> Grid</Button>
            <Button
              variant={view === 'list' ? 'default' : 'outline'} size="sm"
              onClick={() => setView('list')} data-testid="view-list"
            ><List className="w-4 h-4 mr-1" /> List</Button>
            <Button variant="outline" size="sm" onClick={handlePrint} data-testid="print-face-sheet">
              <Printer className="w-4 h-4 mr-1" /> Print / PDF
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card className="mb-4 no-print">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div>
                <Label className="text-xs mb-1 block">Search</Label>
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
                  <Input className="pl-8 h-9 text-sm" placeholder="Search by name…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="roster-search" />
                </div>
              </div>
              <div>
                <Label className="text-xs mb-1 block">Squad</Label>
                <Select value={squad || 'all'} onValueChange={(v) => setSquad(v === 'all' ? '' : v)}>
                  <SelectTrigger className="h-9 text-sm" data-testid="roster-squad"><SelectValue placeholder="All" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Squads</SelectItem>
                    {SQUADS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs mb-1 block">Position</Label>
                <Select value={position || 'all'} onValueChange={(v) => setPosition(v === 'all' ? '' : v)}>
                  <SelectTrigger className="h-9 text-sm" data-testid="roster-position"><SelectValue placeholder="All" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Positions</SelectItem>
                    {distinctPositions.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs mb-1 block">Nationality</Label>
                <Select value={nationality || 'all'} onValueChange={(v) => setNationality(v === 'all' ? '' : v)}>
                  <SelectTrigger className="h-9 text-sm" data-testid="roster-nationality"><SelectValue placeholder="All" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {distinctNationalities.map(n => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="text-xs text-gray-500 mb-3 no-print">{loading ? 'Loading…' : `${athletes.length} athlete${athletes.length === 1 ? '' : 's'}`}</div>

        {/* Grid view */}
        {view === 'grid' && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="roster-grid">
            {athletes.map(a => (
              <div
                key={a.id}
                role="button"
                tabIndex={0}
                onClick={() => openAthlete(a)}
                onKeyDown={(e) => { if (e.key === 'Enter') openAthlete(a); }}
                className="group relative print-card text-left bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 cursor-pointer hover:shadow-md transition focus:outline-none focus:ring-2 focus:ring-emerald-500"
                data-testid={`roster-card-${a.id}`}
              >
                {canManagePhotos && (
                  <RosterPhotoButton
                    athleteId={a.id}
                    athleteName={a.full_name}
                    onUploaded={(fields) => handleUploaded(a.id, fields)}
                  />
                )}
                <div className="flex flex-col items-center text-center">
                  <AthleteAvatar photoUrl={a.profile_photo} thumbUrl={a.profile_photo_thumbnail} name={a.full_name} size="lg" />
                  <p className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100 truncate w-full">{a.full_name}</p>
                  <div className="flex flex-wrap items-center justify-center gap-1 mt-1">
                    {a.squad && <Badge variant="outline" className="text-[10px] px-1 py-0">{a.squad}</Badge>}
                    {a.position && <Badge variant="outline" className="text-[10px] px-1 py-0">{a.position}</Badge>}
                  </div>
                  <p className="text-[11px] text-gray-500 mt-1">
                    {a.nationality || '—'}{a.date_of_birth ? ` · ${ageFromDob(a.date_of_birth)}y` : ''}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* List view */}
        {view === 'list' && (
          <Card data-testid="roster-list">
            <CardContent className="p-0">
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {athletes.map(a => (
                  <div
                    key={a.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => openAthlete(a)}
                    onKeyDown={(e) => { if (e.key === 'Enter') openAthlete(a); }}
                    className="print-card w-full text-left px-4 py-2 flex items-center gap-3 cursor-pointer hover:bg-gray-50/80 dark:hover:bg-gray-800/50 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    data-testid={`roster-row-${a.id}`}
                  >
                    <AthleteAvatar photoUrl={a.profile_photo} thumbUrl={a.profile_photo_thumbnail} name={a.full_name} size="sm" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{a.full_name}</p>
                      <p className="text-xs text-gray-500 truncate">{[a.squad, a.position, a.nationality].filter(Boolean).join(' · ')}</p>
                    </div>
                    {canManagePhotos && (
                      <div className="flex-shrink-0 no-print" onClick={(e) => e.stopPropagation()}>
                        <RosterPhotoButton
                          athleteId={a.id}
                          athleteName={a.full_name}
                          variant="inline"
                          onUploaded={(fields) => handleUploaded(a.id, fields)}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {!loading && athletes.length === 0 && (
          <div className="text-center py-12 text-gray-500">No athletes match the filters.</div>
        )}
      </div>
    </Layout>
  );
};

export default VisualRoster;
